import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { PlusOutlined } from '@ant-design/icons';
import { Upload } from 'antd';

import axios from 'axios';
import config from '../../config';

import '../../styles/buttons.css';
import '../../styles/popup.css';

import imageUpload from '../general/imageUpload';
import { setUser } from '../../store/userSlice';
import { cacheProfilePicture } from '../../store/cacheProfile'

const getBase64 = (img, callback) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result));
  reader.readAsDataURL(img);
};

const EditProfileForm = ({ onClose, user, account, property }) => {
  const [imageUrl, setImageUrl] = useState();
  const [imageFile, setImageFile] = useState(null);
  const dispatch = useDispatch();

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type="button">
      <PlusOutlined />
      <div className="font-poppins" style={{ marginTop: 4 }}>Upload</div>
    </button>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      toast.error("Please select an image first.");
      return;
    }

    try {
      const uploadedIds = await imageUpload(
        [imageFile].map(f => ({ name: f.name, type: f.type, data: f })),
        'profile',
        property,
        account,
        ['profile picture']
      );

      const uploadedId = uploadedIds[0];

      // 🔑 Update user with new profile picture ID
      await axios.put(
        `${config.API_URL}/v2/user/email/${user.email}`,
        { profilePicture: uploadedId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );

      // Refresh user data
      const refreshed = await axios.get(
        `${config.API_URL}/v2/user/email/${user.email}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (refreshed.data) {
        // 🔥 Update Redux
        dispatch(setUser(refreshed.data));

        // 🔥 Re-cache the new profile picture (avoids showing the old one)
        const newPicKey = refreshed.data.profilePicture?.key;
        if (newPicKey) {
          await cacheProfilePicture(newPicKey, refreshed.data._id);
        }
      }

      toast.success("Profile picture updated successfully!");
      onClose();
    } catch (err) {
      console.error("❌ Error updating profile picture:", err);
      toast.error("Failed to update profile picture.");
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <button className="close-popup" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <h2 className="text-2xl text-black">Edit Profile Picture</h2>
        <p className="popup-msg" style={{ marginBottom: "20px" }}>
          Please upload a JPG or PNG file (max 2MB).
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem' }}>
            <Upload
              name="avatar"
              listType="picture-circle"
              className="avatar-uploader"
              showUploadList={false}
              beforeUpload={(file) => {
                const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
                if (!isJpgOrPng) {
                  toast.error('You can only upload JPG/PNG file!');
                  return Upload.LIST_IGNORE;
                }
                const isLt2M = file.size / 1024 / 1024 < 2;
                if (!isLt2M) {
                  toast.error('Image must be smaller than 2MB!');
                  return Upload.LIST_IGNORE;
                }
                getBase64(file, (url) => {
                  setImageUrl(url);
                });
                setImageFile(file); // store file for upload
                return false; // prevent auto-upload
              }}
            >
              {imageUrl ? (
                <img src={imageUrl} alt="avatar" style={{ width: '100%' }} />
              ) : (
                uploadButton
              )}
            </Upload>
          </div>
          <div className="form-buttons">
            <button type="submit" className="button">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileForm;
